import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  TextField, Typography, Alert, CircularProgress, Chip, Link, Tooltip,
  ToggleButtonGroup, ToggleButton, Autocomplete, Switch, FormControlLabel, Checkbox,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VideocamIcon from '@mui/icons-material/Videocam';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';

import {
  parseMeetingBooking, buildFlatboxMeeting, buildIcs, icsFilename, downloadIcs, loadMeetingSignature,
  MEETING_DEFAULT_MINUTES, MEETING_TIMEZONE, SUBJECT_PREFIX,
} from '../../utils/outlookMeeting';
import { isGraphConfigured } from '../../services/msGraphConfig';

// MSAL laddas först när Outlook-kopplingen används, inte i huvudbundeln.
const graph = () => import('../../services/msGraph');

const EMPTY_FORM = {
  customerName: '', contactName: '', contactEmail: '',
  date: '', time: '', durationMinutes: MEETING_DEFAULT_MINUTES,
  roomName: '', roomEmail: '', isTeams: true,
};

/**
 * Bokar ett FLATBOX-möte i Outlook utifrån ett inklistrat inbokningsmail.
 * Skickas via Microsoft Graph när kopplingen är konfigurerad, annars laddas
 * mötet ner som .ics-fil att öppna i Outlook.
 *
 * contacts         kundens kontakter
 * linkedContactIds kontakter kopplade till leadet — visas först
 * defaultContactIds förvalda deltagare (normalt leadets huvudkontakt)
 */
export default function OutlookBookingDialog({
  open, onClose, currentUser, defaultCustomerName = '',
  contacts = [], linkedContactIds = [], defaultContactIds = [], onBooked,
}) {
  const [paste, setPaste] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mode, setMode] = useState('send'); // 'send' = skicka inbjudan, 'hold' = bara boka tiden
  const [signature, setSignature] = useState({ html: '', text: '', senderName: '', senderEmail: '' });
  const [account, setAccount] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsBlocked, setRoomsBlocked] = useState(false);

  const graphAvailable = isGraphConfigured();

  // Nollställ och förifyll när dialogen öppnas
  useEffect(() => {
    if (!open) return;
    setPaste('');
    setError('');
    setResult(null);
    // Utan Outlook-koppling finns ingen väg att skicka inbjudan — .ics-filen
    // laddas bara ner. Då ska läget inte gå att välja.
    setMode(graphAvailable ? 'send' : 'hold');
    setRooms([]);
    setRoomsBlocked(false);
    setForm({ ...EMPTY_FORM, customerName: defaultCustomerName || '' });
    setSelectedIds(defaultContactIds.filter(id => contacts.some(c => c.id === id && c.email)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kopplade kontakter först, sedan övriga i namnordning.
  const sortedContacts = [...contacts].sort((a, b) => {
    const la = linkedContactIds.includes(a.id) ? 0 : 1;
    const lb = linkedContactIds.includes(b.id) ? 0 : 1;
    return la - lb || (a.name || '').localeCompare(b.name || '', 'sv');
  });
  const selectedContacts = contacts
    .filter(c => selectedIds.includes(c.id) && c.email)
    .map(c => ({ name: c.name, email: c.email, contactId: c.id }));
  const toggleContact = id => setSelectedIds(ids =>
    ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  // Signaturen hämtas från samma källa som Signature-sidan
  useEffect(() => {
    if (!open || !currentUser) return;
    let cancelled = false;
    loadMeetingSignature(currentUser).then(s => { if (!cancelled) setSignature(s); });
    return () => { cancelled = true; };
  }, [open, currentUser]);

  // Befintlig Outlook-anslutning
  useEffect(() => {
    if (!open || !graphAvailable) return;
    graph().then(g => g.getConnectedAccount()).then(setAccount).catch(() => setAccount(null));
  }, [open, graphAvailable]);

  // Rumslistan kräver Place.Read.All. Den frågas först tyst — går det inte
  // utan ett nytt samtycke får säljaren en knapp i stället för en popup som
  // hoppar upp oombedd (och som webbläsaren gärna blockerar).
  const loadRooms = useCallback(async ({ silent }) => {
    setRoomsLoading(true);
    try {
      const { listRooms } = await graph();
      setRooms(await listRooms({ silent }));
      setRoomsBlocked(false);
    } catch {
      setRooms([]);
      setRoomsBlocked(true);
    }
    setRoomsLoading(false);
  }, []);

  useEffect(() => {
    if (!open || !graphAvailable || !account) return;
    loadRooms({ silent: true });
  }, [open, graphAvailable, account, loadRooms]);

  const handlePaste = useCallback(val => {
    setPaste(val);
    const parsed = parseMeetingBooking(val);
    if (!parsed) return;
    // Finns personen redan bland kundens kontakter kryssas den i i stället för
    // att hamna som fritextmottagare.
    const known = parsed.contactEmail
      && contacts.find(c => (c.email || '').toLowerCase() === parsed.contactEmail.toLowerCase());
    if (known) {
      setSelectedIds(ids => (ids.includes(known.id) ? ids : [...ids, known.id]));
      parsed.contactName = '';
      parsed.contactEmail = '';
    }
    // Tolkade värden fyller bara tomma fält — det du redan justerat får stå kvar
    setForm(f => ({
      ...f,
      customerName: f.customerName || parsed.customerName,
      contactName:  f.contactName  || parsed.contactName,
      contactEmail: f.contactEmail || parsed.contactEmail,
      date:         f.date         || parsed.date,
      time:         f.time         || parsed.time,
    }));
  }, [contacts]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const meeting = buildFlatboxMeeting({
    ...form,
    durationMinutes: Number(form.durationMinutes) || MEETING_DEFAULT_MINUTES,
    roomName: form.roomName,
    roomEmail: form.roomEmail,
    isTeams: form.isTeams,
    signatureHtml: signature.html,
    signatureText: signature.text,
    senderName: signature.senderName,
    contacts: selectedContacts,
  });

  const sendsInvitation = mode === 'send';
  const ready = !!(
    form.date && form.time &&
    (form.customerName || form.contactName || selectedContacts.length) &&
    (!sendsInvitation || meeting.attendees.length > 0)
  );

  async function handleConnect() {
    setConnecting(true);
    setError('');
    try {
      const { connectOutlook } = await graph();
      setAccount(await connectOutlook(signature.senderEmail));
    } catch (err) {
      if (err?.errorCode !== 'user_cancelled') {
        setError(err?.message || 'Kunde inte ansluta till Outlook.');
      }
    }
    setConnecting(false);
  }

  async function handleBook() {
    setSending(true);
    setError('');
    try {
      const { createOutlookEvent } = await graph();
      const created = await createOutlookEvent(meeting, MEETING_TIMEZONE, {
        sendInvitation: sendsInvitation,
      });
      const outcome = { ...created, via: 'graph', sent: sendsInvitation };
      setResult(outcome);
      onBooked?.(meeting, outcome);
    } catch (err) {
      setError(err?.message || 'Kunde inte skapa mötet i Outlook.');
    }
    setSending(false);
  }

  function handleDownloadIcs() {
    const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const uid = `flatbox-${stamp}-${Math.random().toString(36).slice(2, 10)}@lagbasn`;
    // En nedladdad fil skickar aldrig något av sig själv, så den byggs alltid
    // som kalenderhändelse (METHOD:PUBLISH) utan deltagare. En inbjudan
    // (METHOD:REQUEST) vägrar Outlook dessutom att importera — kunden läggs
    // till i mötet när du öppnat det i Outlook.
    const ics = buildIcs(meeting, {
      organizerName: signature.senderName,
      organizerEmail: signature.senderEmail,
      uid, stamp,
      includeAttendees: false,
    });
    downloadIcs(ics, icsFilename(meeting));
    onBooked?.(meeting, { via: 'ics', sent: false });
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EventIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Boka {SUBJECT_PREFIX}-möte</Typography>
            <Typography variant="caption" color="text.secondary">
              Teams-möte i din Outlook-kalender med inbjudan till kunden
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {result ? (
            <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {result.sent
                  ? 'Mötet är bokat och inbjudan skickad.'
                  : 'Mötet ligger i din kalender. Ingen inbjudan har skickats.'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                {meeting.subject} — {meeting.date} kl. {meeting.time}
              </Typography>
              {result.joinUrl && (
                <Link href={result.joinUrl} target="_blank" rel="noopener"
                  sx={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <VideocamIcon sx={{ fontSize: 16 }} /> Teams-länk
                </Link>
              )}
            </Alert>
          ) : (
            <>
              {/* ── Snabbfyll från inbokningsmail ── */}
              <Box sx={{ bgcolor: '#fff8e1', borderRadius: 2, p: 2, border: '1px solid #ffe082' }}>
                <Typography variant="caption" fontWeight="bold"
                  sx={{ color: '#E65100', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Snabbfyll — klistra in inbokningsmailet
                </Typography>
                <TextField fullWidth size="small" multiline minRows={2}
                  placeholder="Klistra in hela inbokningsmailet här"
                  value={paste}
                  onChange={e => handlePaste(e.target.value)}
                  helperText="Namn, e-post, datum och tid fylls i automatiskt — justera vid behov"
                  InputProps={{ sx: { bgcolor: 'white' } }}
                />
              </Box>

              <TextField label="Kundnamn" size="small" fullWidth
                value={form.customerName}
                onChange={e => set('customerName', e.target.value)}
                helperText={`Ämnesrad: ${meeting.subject}`}
              />

              {contacts.length > 0 && (
                <Box>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Deltagare hos kunden
                  </Typography>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 200, overflow: 'auto' }}>
                    {sortedContacts.map(c => {
                      const linked = linkedContactIds.includes(c.id);
                      return (
                        <FormControlLabel key={c.id} sx={{ display: 'flex', mx: 0, px: 1, '&:hover': { bgcolor: '#fafafa' } }}
                          disabled={!c.email}
                          control={<Checkbox size="small" checked={selectedIds.includes(c.id) && !!c.email}
                            onChange={() => toggleContact(c.id)} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2">{c.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {c.email || 'saknar e-post'}{c.role ? ` · ${c.role}` : ''}
                              </Typography>
                              {linked && <Chip size="small" label="Kopplad till leadet"
                                sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#FFF3E0', color: '#E65100' }} />}
                            </Box>
                          } />
                      );
                    })}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label={contacts.length ? 'Annan mottagare (valfritt)' : 'Kontaktperson'} size="small" sx={{ flex: 1 }}
                  value={form.contactName}
                  onChange={e => set('contactName', e.target.value)} />
                <TextField label="E-post" size="small" type="email" sx={{ flex: 1 }}
                  value={form.contactEmail}
                  onChange={e => set('contactEmail', e.target.value)} />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Datum" type="date" size="small" sx={{ flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                  value={form.date}
                  onChange={e => set('date', e.target.value)} />
                <TextField label="Tid" type="time" size="small" sx={{ flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  value={form.time}
                  onChange={e => set('time', e.target.value)} />
                <TextField label="Längd (min)" type="number" size="small" sx={{ width: 120 }}
                  inputProps={{ min: 15, step: 15 }}
                  value={form.durationMinutes}
                  onChange={e => set('durationMinutes', e.target.value)} />
              </Box>

              {/* ── Plats: rum och/eller Teams ── */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Autocomplete
                  freeSolo size="small" sx={{ flex: 1 }}
                  options={rooms}
                  loading={roomsLoading}
                  getOptionLabel={o => (typeof o === 'string' ? o : o.name)}
                  isOptionEqualToValue={(o, v) => o.email === v.email}
                  inputValue={form.roomName}
                  onInputChange={(e, val, reason) => {
                    // Skriver man själv är kopplingen till ett valt rum bruten,
                    // och därmed också rumsbokningen.
                    if (reason === 'input') setForm(f => ({ ...f, roomName: val, roomEmail: '' }));
                  }}
                  onChange={(e, val) => {
                    if (val && typeof val === 'object') {
                      setForm(f => ({ ...f, roomName: val.name, roomEmail: val.email }));
                    } else {
                      setForm(f => ({ ...f, roomName: val || '', roomEmail: '' }));
                    }
                  }}
                  renderOption={(props, o) => (
                    <li {...props} key={o.email}>
                      <Box>
                        <Typography variant="body2">{o.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[o.building, o.floor && `plan ${o.floor}`,
                            o.capacity && `${o.capacity} platser`].filter(Boolean).join(' · ') || o.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={params => (
                    <TextField {...params} label="Mötesrum (valfritt)"
                      helperText={form.roomEmail
                        ? 'Rummet bokas som resurs — Outlook bekräftar när det är ledigt'
                        : rooms.length
                          ? 'Välj ur listan för att boka rummet, eller skriv en plats i klartext'
                          : 'Skriv platsen i klartext — utan rumslistan bokas inget rum automatiskt'}
                    />
                  )}
                />
                <Box sx={{ pt: 0.5, minWidth: 150 }}>
                  <FormControlLabel
                    control={<Switch size="small" checked={form.isTeams}
                      onChange={e => set('isTeams', e.target.checked)} />}
                    label={<Typography variant="body2">Teams-möte</Typography>}
                  />
                  {graphAvailable && account && roomsBlocked && (
                    <Button size="small" startIcon={<MeetingRoomIcon />}
                      disabled={roomsLoading}
                      onClick={() => loadRooms({ silent: false })}
                      sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                      {roomsLoading ? 'Hämtar…' : 'Hämta rumslistan'}
                    </Button>
                  )}
                </Box>
              </Box>

              {/* ── Skicka eller bara boka tiden ── */}
              {graphAvailable && (
              <Box>
                <ToggleButtonGroup exclusive fullWidth size="small"
                  value={mode}
                  onChange={(e, val) => val && setMode(val)}>
                  <ToggleButton value="send">Skapa och skicka inbjudan</ToggleButton>
                  <ToggleButton value="hold">Bara skapa mötet</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}>
                  {sendsInvitation
                    ? 'Mötet läggs i din kalender och inbjudan går ut till kunden direkt.'
                    : `Mötet läggs i din kalender${form.isTeams ? ' med Teams-länk' : ''}, utan deltagare. Ingen inbjudan skickas — du lägger till kunden och skickar själv från Outlook.${form.roomEmail ? ' Rummet bokas ändå, det får en förfrågan direkt.' : ''}`}
                </Typography>
              </Box>
              )}

              {/* ── Vad som skapas ── */}
              <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {sendsInvitation ? 'Inbjudan' : 'Kalenderhändelse'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {meeting.isTeams && <Chip size="small" icon={<VideocamIcon />} label="Teams-möte" />}
                  {meeting.room && (
                    <Chip size="small" icon={<MeetingRoomIcon />} label={meeting.room.name}
                      variant={meeting.room.email ? 'filled' : 'outlined'} />
                  )}
                  <Chip size="small" label={`${meeting.durationMinutes} min`} />
                  <Chip size="small" label={MEETING_TIMEZONE} />
                  {sendsInvitation
                    ? meeting.attendees.map(a => (
                        <Chip key={a.email} size="small" label={a.email} variant="outlined" />
                      ))
                    : <Chip size="small" label="Inga deltagare" variant="outlined" />}
                </Box>
                <Box sx={{
                  bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2,
                  p: 1.5, maxHeight: 180, overflow: 'auto', fontSize: 13,
                }}
                  dangerouslySetInnerHTML={{ __html: meeting.bodyHtml }}
                />
              </Box>

              {/* ── Outlook-anslutning ── */}
              {graphAvailable ? (
                account ? (
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#2E7D32' }}>
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                    Ansluten till Outlook som {account.username}
                  </Typography>
                ) : (
                  <Button size="small" variant="outlined" startIcon={<LinkIcon />}
                    disabled={connecting} onClick={handleConnect}
                    sx={{ alignSelf: 'flex-start', borderColor: '#FF6B00', color: '#FF6B00' }}>
                    {connecting ? 'Ansluter…' : 'Anslut till Outlook'}
                  </Button>
                )
              ) : (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    Outlook-kopplingen är inte konfigurerad ännu. Ladda ner mötet som
                    .ics-fil och öppna den i Outlook — lägg till Teams-länken där.
                  </Typography>
                </Alert>
              )}

              {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
            </>
          )}
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{result ? 'Stäng' : 'Avbryt'}</Button>
        {!result && (
          <>
            <Tooltip title="Dubbelklicka filen så öppnas mötet i Outlook — där lägger du till Teams-länken och kunden, och skickar själv">
              <span>
                <Button variant="outlined" startIcon={<DownloadIcon />}
                  disabled={!ready} onClick={handleDownloadIcs}>
                  Ladda ner .ics
                </Button>
              </span>
            </Tooltip>
            {graphAvailable && (
              <Button variant="contained"
                startIcon={sending
                  ? <CircularProgress size={16} color="inherit" />
                  : (sendsInvitation ? <SendIcon /> : <EventIcon />)}
                disabled={!ready || !account || sending}
                onClick={handleBook}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#E65100' } }}>
                {sending
                  ? 'Bokar…'
                  : (sendsInvitation ? 'Boka och skicka' : 'Boka i kalendern')}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
